import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, OnDestroy, ViewChild, computed, model, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { DashboardService } from '../../dashboard/dashboard.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DtccService } from '../../annuity-lookup/dtcc.service';
import { UniversalSearchService } from '../../universal-search/services/universal-search.service';
import { BookManagementService } from '../book-management.service';

@Component({
  selector: 'app-bm-widget-filters',
  templateUrl: './bm-widget-filters.component.html',
  styleUrls: ['./bm-widget-filters.component.scss'],
  standalone: false,
})
export class BmWidgetFiltersComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  private bmSvc = inject(BookManagementService);
  private changeDetector = inject(ChangeDetectorRef);
  private dbs = inject(DashboardService);
  private dtccSvc = inject(DtccService);
  private route = inject(Router);
  private ss = inject(SessionStorageService);
  private usServ = inject(UniversalSearchService);

  @ViewChild('pickerField') pickerField;
  @Input() viewMode;
  @Input() minDate;
  @Input() defaultRange;

  filteredUnits;
  unitCtrl = new FormControl();
  selectedUnit = '';
  npn = new FormControl();
  selectedNpn = '';
  dateRange = {
    start: '',
    end: '',
  };
  unsubscribe: Subject<any> = new Subject();
  floatingRangeOptions = [
    { value: '7 days', display: 'Last 7 Days' },
    { value: '30 days', display: 'Last 30 Days' },
    { value: '90 days', display: 'Last 90 Days' },
    { value: 'mtd', display: 'Month-to-Date' },
    { value: 'ytd', display: 'Year-to-Date' },
    { value: '12mtd', display: '12 Months (to Date)' },
    { value: 'custom', display: 'Custom Range' },
  ];
  presetRangeControl = new FormControl<any>('');

  dropdownLoading = false;
  dropdownNoResults = false;
  dropdownLoadingStandalone = false;
  dropdownNoResultsStandalone = false;
  userControl = new FormControl();
  userControlStandalone = new FormControl();
  filteredUsers = [];
  filteredUsersStandalone = [];
  fetchingNpns = false;
  chips = signal<string[]>([]);
  separatorKeysCodes: number[] = [ENTER, COMMA];
  currentChip = model('');
  chipOptions = signal([]);
  filteredChips = computed(() => {
    const currentChip = this.currentChip().toLowerCase();
    return currentChip
      ? this.chipOptions().filter(
          chip =>
            chip.Name.toLowerCase().includes(currentChip) ||
            chip.NPN.toLowerCase().includes(currentChip)
        )
      : this.chipOptions().slice();
  });
  moreFilters = [
    { label: 'Account Type', fieldVar: 'accountType', value: signal([]) },
    { label: 'Annuity Type', fieldVar: 'productType', value: signal([]) },
    { label: 'Carrier', fieldVar: 'carrier', value: signal([]) },
    { label: 'Product', fieldVar: 'productName', value: signal([]) },
  ];
  filtersUpdated = false;
  homeOfficeUser: boolean = false;
  selectedStandaloneUser = null;

  ngOnInit(): void {
    const globals = this.ss.get('globals');
    if (globals.user.managerUnitCount > 0) {
      this.homeOfficeUser = true;
    }
    this.filteredUnits = globals?.user.managerUnits;
    if (this.filteredUnits && this.filteredUnits.length > 0) {
      this.getUnits();
    }
    this.userControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        this.dropdownLoading = true;
        if (value && typeof value === 'string') {
          return this._filterReps(value);
        } else if (!value) {
          this.filteredUsers = [];
          this.dropdownLoading = false;
        } else {
          this.dropdownLoading = false;
        }
      });
    this.userControlStandalone.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        this.dropdownLoadingStandalone = true;
        if (value && typeof value === 'string') {
          return this._filterRepsStandalone(value);
        } else if (!value) {
          this.filteredUsersStandalone = [];
          this.dropdownLoadingStandalone = false;
        } else {
          this.dropdownLoadingStandalone = false;
        }
      });

    if (this.viewMode === 'rep') {
      this.fetchingNpns = true;
      this.dtccSvc.getNPNs(globals.user.id).subscribe(resp => {
        this.fetchingNpns = false;
        this.chipOptions.set(resp.npns);
      });
    }

    if (this.defaultRange) {
      this.presetRangeControl.setValue(this.defaultRange);

      this.calculateDateRange(this.defaultRange);
    }
  }

  ngAfterViewInit(): void {
    this.triggerSearch();
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getUnits() {
    this.unitCtrl.setValue(this.filteredUnits[0].id);
    this.selectedUnit = this.filteredUnits[0].id;
    this.unitCtrl.valueChanges.pipe(distinctUntilChanged()).subscribe(unit => {
      this._filterUnits(unit);
    });
    this.npn.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(npnInput => {
        this.selectedNpn = npnInput;
      });
  }

  private _filterUnits(value: string) {
    this.dbs.unitSearch(value, true).subscribe(resp => {
      if (resp.names) {
        this.filteredUnits = resp.names;
      } else {
        this.filteredUnits = [];
      }
    });
  }

  private _filterReps(value: string) {
    this.dropdownLoading = true;
    this.usServ.userSearch(value).subscribe(res => {
      this.dropdownLoading = false;
      if (res && res.names.length > 0) {
        this.dropdownNoResults = false;
        this.filteredUsers = res.names;
      } else if (!res || res.names.length === 0) {
        this.dropdownNoResults = true;
        this.changeDetector.detectChanges();
      }
    });
  }

  private _filterRepsStandalone(value: string) {
    this.dropdownLoadingStandalone = true;
    this.usServ.userSearch(value).subscribe(res => {
      this.dropdownLoadingStandalone = false;
      if (res && res.names.length > 0) {
        this.dropdownNoResultsStandalone = false;
        this.filteredUsersStandalone = res.names;
      } else if (!res || res.names.length === 0) {
        this.dropdownNoResultsStandalone = true;
        this.changeDetector.detectChanges();
      }
    });
  }

  nav(location) {
    this.route.navigate([`/${location}`]);
  }

  selectUnit(ev) {
    this.unitCtrl.setValue(ev.option.value);
    this.selectedUnit = ev.option.value;
    this.filtersUpdated = true;
  }

  rangeSelection(ev) {
    const { value } = ev;
    const endDateToday = DateTime.now().toUTC().toISO();
    if (value === 'custom') {
      setTimeout(() => {
        this.pickerField.open();
      });
    } else if (value === 'mtd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .startOf('month')
        .toUTC()
        .toISO();
      this.dateRange.start = calculatedStartDate;
      this.dateRange.end = endDateToday;
    } else if (value === 'ytd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .startOf('year')
        .toUTC()
        .toISO();
      this.dateRange.start = calculatedStartDate;
      this.dateRange.end = endDateToday;
    } else if (value === '12mtd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .minus({ years: 1 })
        .startOf('month')
        .toUTC()
        .toISO();
      this.dateRange.start = calculatedStartDate;
      this.dateRange.end = endDateToday;
    } else {
      this.calculateDateRange(value);
    }
    this.filtersUpdated = true;
  }

  dateChange(ev, position) {
    this.presetRangeControl.setValue('custom');
    if (position === 'start') {
      if (
        this.minDate &&
        DateTime.fromJSDate(new Date(ev.value)).startOf('day').toUTC().toISO() <
          this.minDate
      ) {
        this.dateRange[position] = this.minDate;
      } else {
        this.dateRange[position] = DateTime.fromJSDate(new Date(ev.value))
          .startOf('day')
          .toUTC()
          .toISO()
          .toLocaleString();
      }
    } else {
      if (ev.value) {
        this.dateRange[position] = DateTime.fromJSDate(new Date(ev.value))
          .endOf('day')
          .toUTC()
          .toISO();
      } else {
        this.dateRange[position] = DateTime.now().endOf('day').toUTC().toISO();
      }
    }
    this.filtersUpdated = true;
  }

  clearDateField(ev?) {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    this.dateRange = {
      start: '',
      end: '',
    };
    this.presetRangeControl.setValue('');

    if (this.viewMode === 'carrier') {
      this.filtersUpdated = false;
    }
    this.filtersUpdated = true;
  }

  calculateDateRange(value) {
    // Calculate time difference
    const calculateTime = value.split(' ')[0];
    const calculateUnit = value.split(' ')[1];
    const timeDiffObject = {};
    timeDiffObject[calculateUnit] = calculateTime;
    const calculatedStartDate = DateTime.now()
      .minus(timeDiffObject)
      .startOf('day')
      .toUTC()
      .toISO();
    const endDateToday = DateTime.now().endOf('day').toUTC().toISO();
    // Set the date picker with the selected times
    this.dateRange.start = calculatedStartDate;
    this.dateRange.end = endDateToday;
  }

  clearRepSearch() {
    this.userControl.setValue('');
    this.chips.update(() => []);
    this.currentChip.set('');
  }

  clearUserStandalone() {
    this.userControlStandalone.setValue('');
    this.selectedStandaloneUser = null;
    this.filtersUpdated = true;
  }

  clearNpnSearch() {
    this.currentChip.set('');
  }

  removeNpn(npn: string): void {
    this.chips.update((chips: string[]) => {
      const index = chips.indexOf(npn);
      if (index < 0) {
        return chips;
      }

      chips.splice(index, 1);
      return [...chips];
    });
    this.filtersUpdated = true;
  }

  npnSelected(event: MatAutocompleteSelectedEvent): void {
    if (!this.chips().includes(event.option.value)) {
      this.chips.update(chips => [...chips, event.option.value]);
    }
    this.currentChip.set('');
    event.option.deselect();
    this.filtersUpdated = true;
  }

  chipDisplay(val) {
    const dispChip = this.chipOptions().find(opt => opt.NPN === val);
    if (dispChip) {
      return `${dispChip.Name} - ${dispChip.NPN}`;
    }

    return val;
  }

  displayUser(val) {
    return val ? `${val.name}` : '';
  }

  changeUserStandalone(ev) {
    this.filtersUpdated = true;
    this.selectedStandaloneUser = this.userControlStandalone.value.id;
  }

  changeSelection(ev) {
    this.fetchingNpns = true;
    this.dtccSvc.getNPNs(ev.option.value.id).subscribe(resp => {
      this.fetchingNpns = false;
      this.chipOptions.set(resp.npns);
    });
  }

  triggerSearch() {
    const bmWidgetFilters = {
      unitId: this.selectedUnit,
      userId: [this.selectedStandaloneUser],
      npn: this.chips(),
      start: this.dateRange.start,
      end: this.dateRange.end,
    };

    this.moreFilters.map(bmFilter => {
      if (bmFilter.value() && bmFilter.value().length > 0) {
        bmWidgetFilters[bmFilter.fieldVar] = bmFilter.value();
      }
    });

    this.bmSvc.updateBmWidgetFilters(bmWidgetFilters);

    this.filtersUpdated = false;
  }

  moreFilterCount() {
    let moreFilterCount = 0;

    this.moreFilters.map(bmFilter => {
      if (bmFilter.value() && bmFilter.value().length > 0) {
        moreFilterCount++;
      }
    });

    return moreFilterCount;
  }

  resetFilters() {
    this.bmSvc.clearBmWidgetFilters(null);

    if (this.defaultRange) {
      this.presetRangeControl.setValue(this.defaultRange);

      this.calculateDateRange(this.defaultRange);
    } else {
      this.clearDateField();
    }

    this.clearRepSearch();
    this.chips.update(() => []);
    this.currentChip.set('');
    this.clearUserStandalone();
    this.triggerSearch();
  }

  toggleFiltersUpdated(value) {
    this.filtersUpdated = value;
  }
}
